/**
 * Enumerate the commands a seat may send right now. The client uses this to
 * enable buttons; the random-playout bot in the tests uses it to drive games;
 * the server uses `isLegal` as a cheap pre-check before `apply`.
 *
 * Deliberately conservative: a command listed here must be accepted by
 * `apply`. (A property test enforces that.)
 */
import { constructionOptions } from './actions/construction.js';
import { forumOptions } from './actions/forum.js';
import { militaryOptions } from './actions/military.js';
import { seaportOptions } from './actions/seaport.js';
import { canAdvanceSenate } from './actions/senate.js';
import { extraActionOptions } from './extraActions.js';
import { demandOptions } from './quarterEnd.js';
import { canTakeTrajanTile } from './actions/trajan.js';
import { MANIFESTS } from './manifests.js';
import { apply, decisionMaker } from './reducer.js';
import { previewTray } from './tray.js';
import { ACTIONS, TRAY_COUNT } from './types.js';
/**
 * Every command the given seat may send right now.
 *
 * The contract, enforced by `test/legalContract.test.ts`:
 *   - **sound** — `apply` accepts every command returned here;
 *   - **complete by command type** — if `apply` would accept any command of a
 *     type in this phase, at least one command of that type is returned, so no
 *     option is ever hidden from a player;
 *   - **representative payloads** — two families are deliberately narrowed,
 *     because their payload space is combinatorial and the choice belongs to
 *     the client: `SelectTray` is returned without a `colorOrder` (any
 *     permutation of the tray is legal), and the seaport `display` option lists
 *     only the first one or two cards (any one or two hand cards are legal).
 */
export function legalCommands(state, actor) {
    const phase = state.phase;
    if (phase.kind === 'gameOver')
        return [];
    const player = state.players.find((p) => p.id === actor);
    if (!player || decisionMaker(state) !== actor)
        return [];
    switch (phase.kind) {
        case 'setup.arrangeMarkers':
            // Any valid arrangement is legal; offer the current (default) layout as the canonical option.
            return [{ type: 'ArrangeMarkers', trays: player.trays.map((t) => [...t]) }];
        case 'setup.chooseTrajanTiles': {
            const rule = MANIFESTS.setup.initialTrajanTiles;
            const slots = rule.slots;
            const stacks = state.board.trajanStacks
                .map((s, i) => (s.length > 0 ? i : -1))
                .filter((i) => i >= 0);
            const out = [];
            for (let a = 0; a < stacks.length; a++)
                for (let b = a + 1; b < stacks.length; b++)
                    for (let c = b + 1; c < stacks.length; c++) {
                        out.push({
                            type: 'ChooseInitialTrajanTiles',
                            picks: [
                                { stack: stacks[a], slot: slots[0] },
                                { stack: stacks[b], slot: slots[1] },
                                { stack: stacks[c], slot: slots[2] },
                            ],
                        });
                    }
            return out;
        }
        case 'awaitTray': {
            const out = [];
            for (let t = 0; t < TRAY_COUNT; t++) {
                const tray = t;
                if (previewTray(player, tray, MANIFESTS.trajanSlotBesideTray))
                    out.push({ type: 'SelectTray', tray });
            }
            return out;
        }
        case 'awaitTrajanDecision': {
            const tile = player.trajanSlots[phase.slot];
            const out = [{ type: 'AccomplishTrajanTile', accept: false }];
            if (!tile)
                return out;
            if (tile.kind === 'plus2') {
                for (const space of ACTIONS) {
                    if (!player.plus2Spaces.includes(space))
                        out.push({ type: 'AccomplishTrajanTile', accept: true, plus2Space: space });
                }
            }
            else {
                out.push({ type: 'AccomplishTrajanTile', accept: true });
            }
            return out;
        }
        case 'awaitAction': {
            const out = [];
            if (phase.action === 'trajan' && canTakeTrajanTile(player)) {
                state.board.trajanStacks.forEach((stack, i) => {
                    if (stack.length > 0)
                        out.push({ type: 'TakeAction', kind: 'trajan', payload: { stack: i } });
                });
            }
            if (phase.action === 'forum') {
                for (const payload of forumOptions(state.board))
                    out.push({ type: 'TakeAction', kind: 'forum', payload });
            }
            if (phase.action === 'military') {
                for (const payload of militaryOptions(state, player))
                    out.push({ type: 'TakeAction', kind: 'military', payload });
            }
            if (phase.action === 'senate' && canAdvanceSenate(player)) {
                out.push({ type: 'TakeAction', kind: 'senate', payload: {} });
            }
            if (phase.action === 'construction') {
                for (const payload of constructionOptions(state, player))
                    out.push({ type: 'TakeAction', kind: 'construction', payload });
            }
            if (phase.action === 'seaport') {
                for (const payload of seaportOptions(state, player))
                    out.push({ type: 'TakeAction', kind: 'seaport', payload });
            }
            out.push({ type: 'SkipAction' }, { type: 'EndTurn' });
            return out;
        }
        case 'awaitSeaportDiscard': {
            const out = [];
            for (const card of player.hand ?? []) {
                out.push({ type: 'DiscardCard', cardId: card.id, pile: 0 }, { type: 'DiscardCard', cardId: card.id, pile: 1 });
            }
            return out;
        }
        case 'awaitExtraAction':
            return [...extraActionOptions(state, player), { type: 'EndTurn' }];
        case 'quarterEnd.demands': {
            const icon = state.demands[phase.demandIndex]?.icon;
            if (!icon)
                return [];
            const used = state.quarterEnd?.usedTrajanTiles[String(actor)] ?? [];
            return demandOptions(player, icon, used).map((o) => ({
                type: 'SatisfyDemand',
                demandIndex: phase.demandIndex,
                tileId: o.tileId,
            }));
        }
        case 'quarterEnd.consulChoice': {
            const out = [];
            if (state.board.senateBonus[0])
                out.push({ type: 'ChooseBonusTile', side: 0 });
            if (state.board.senateBonus[1])
                out.push({ type: 'ChooseBonusTile', side: 1 });
            return out;
        }
    }
}
export function isLegal(state, command, actor) {
    return apply(state, command, actor).ok;
}
//# sourceMappingURL=legal.js.map